// src/globalfunction.js
import { iName, tokenName } from "./constant";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import Cookies from "js-cookie";

const AUTH_HEADER = "x-id-token";

/** Safely test if a JWT is expired (or invalid). */
const isExpired = (token) => {
  try {
    const { exp } = jwtDecode(token || "");
    return !exp || exp < Date.now() / 1000; // no exp → treat as expired
  } catch {
    return true; // invalid token
  }
};

/** Shared cookie options (transitional, JS-readable). */
const getCookieOptions = () => {
  const isHttps =
    typeof window !== "undefined" && window.location?.protocol === "https:";
  return {
    path: "/", // ✅ available for whole app
    sameSite: "lax",
    secure: isHttps, // ✅ true on HTTPS, false on localhost HTTP
  };
};

/** Remove auth info from cookies and Axios header. */
export const clearAuth = () => {
  try {
    const opts = getCookieOptions();
    Cookies.remove(tokenName, opts);
    Cookies.remove(iName, opts);

    // ✅ Backward cleanup: if older cookie set without options/path
    Cookies.remove(tokenName);
    Cookies.remove(iName);
  } catch {
    // ignore
  }

  delete axios.defaults.headers.common[AUTH_HEADER];
};

/**
 * Get a valid token from cookies.
 * - Returns `null` if missing/expired/invalid.
 * - Optionally attaches the Axios auth header.
 */
export const getToken = (setHeader = true) => {
  let token = null;

  try {
    token = Cookies.get(tokenName) || null;
  } catch {
    token = null;
  }

  if (!token || isExpired(token)) {
    clearAuth();
    return null;
  }

  if (setHeader) {
    axios.defaults.headers.common[AUTH_HEADER] = token;
  }

  return token;
};

/**
 * Get the stored display name **only if** the token is valid.
 * Returns `null` otherwise.
 */
export const getName = () => {
  const token = getToken(false);
  if (!token) return null;

  try {
    return Cookies.get(iName) || null;
  } catch {
    return null;
  }
};

/** Convenience helper: ensure Axios header is set if token is valid. */
export const attachAuthHeader = () => Boolean(getToken(true));
